/**
 * Entity Property Utilities
 * 
 * Pure functions for categorizing and processing entity properties for display.
 * Used by EntityDetailDialog and other entity display components.
 */

import { isSetTransition, isWorkflow, isWorkflowActivity, isPhoto } from './entityTypeDetection';
import { isGuid } from '../components/common/GuidDisplay';
import { isSid } from '../components/common/SidDisplay';
import type { EntityRef } from '../types/entity/EntityRef';

import { createEntityRef, getEntityValueFromARef, setEntityValueFromARef, isA, normalizeKey, getEntityValueByEntityRef, entityHasEntityRef } from '../types';
import { debugVerboseLog } from './debugLog';

/**
 * Centralized cache of simple EntityRefs. Each ref is stored under both its normalized id and
 * normalized displayName so lookup finds it whether the user passes something matching id or displayName.
 */
const SIMPLE_ENTITY_REF_CACHE = new Map<string, EntityRef>();

/**
 * Known attribute keys we care about for categorization and UI. Eagerly prepopulate the cache
 * at module load so lookups hit without creating refs on the fly. Not tied to API schema.
 */
const KNOWN_ATTRIBUTE_KEYS = [
    'Id', 'DisplayName', 'ObjectId', 'EntraId', 'MemberTransitive', 'EntityType', 'Type', 'Settings', 'Steps', 'Description', 'Script', 'Action', 'Authorization',
    'MemberOf', 'MemberOfTransitive', 'emails', 'phoneNumbers', 'name', 'meta', 'schemas', 'Data',
    'modification', 'catalogId', 'LegacyFilter', 'Operation', 'Operations', 'Version',
    'SqlStatement', 'SqlStatementExplicit', 'ActionWorkflow', 'TargetSet', 'Config', '$debug', '_query',
    'Body', 'FilterScope', 'PossessiveTargetAttribute', 'PossessiveScope',
    'PossessiveActorsAttribute', 'PossessiveActorsAttributes', 'RelativeTargetAttribute',
    'Attributes', 'AllAttributes', 'ReadEntity', 'RequestorSet', 'TargetPostOpSet', 'RequestWorkflow', 'AuthorizationWorkflow',
    'Direction', 'Scope', 'Tags', 'ContentType', 'Filename', 'Dob', 'AccountName',
    'CompletedApprovalEmail', 'PendingApprovalEmail', 'RejectedRequestEmail', 'ExpiredEmail',
] as const;

/**
 * Normalized keys that are known misspellings. When a key matches (after normalizeKey), we warn.
 */
const COMMON_MISSPELLINGS: readonly string[] = [
    'modfication',
    'possesivescope',
];

/**
 * Stores a ref in the cache under both normalizeKey(ref.id) and normalizeKey(ref.displayName).
 */
function setCachedRef(ref: EntityRef): void {
    const idKey = normalizeKey(ref.id);
    const dnKey = normalizeKey(ref.displayName);
    if (idKey) SIMPLE_ENTITY_REF_CACHE.set(idKey, ref);
    if (dnKey && dnKey !== idKey) SIMPLE_ENTITY_REF_CACHE.set(dnKey, ref);
}

// Eagerly prepopulate cache with known attribute refs at module load
for (const key of KNOWN_ATTRIBUTE_KEYS) {
    setCachedRef(createEntityRef(key));
}

/**
 * Returns a cached EntityRef for the given key, or creates one, stores it under both id and displayName, and returns it.
 * Lookup is by normalizeKey(key), so "Id", "id", and "$.Id" all find the same ref.
 * Exported so callers (e.g. PolicyRuleFormLayout) can use the same refs as isARef/hasFormValueForAttributeRef.
 */
export function getCachedRef(key: string): EntityRef {
    const k = normalizeKey(key);
    if (!k) throw new Error('entityPropertyUtils.getCachedRef: key must not be empty.');
    let ref = SIMPLE_ENTITY_REF_CACHE.get(k);
    if (!ref) {
        ref = createEntityRef(key);
        setCachedRef(ref);
    }
    return ref;
}

/**
 * Like isA but uses the local cache of simple refs for string keys: looks up (or creates and stores) the ref for the key, then calls isA(ref, cachedRef).
 * If the normalized key is in COMMON_MISSPELLINGS, logs a console warning and continues.
 */
/** Re-export so callers can get/set entity values by attribute ref (string or AttributeRef) using DisplayName for keys. */
export { getEntityValueFromARef, setEntityValueFromARef };

export function isARef(ref: EntityRef, keyOrRef: string | EntityRef): boolean {
    if (typeof keyOrRef === 'string') {
        const k = normalizeKey(keyOrRef);
        if (k && COMMON_MISSPELLINGS.includes(k)) {
            console.warn(`[entityPropertyUtils] "${keyOrRef}" is a known misspelling.`);
        }
        const cached = getCachedRef(keyOrRef);
        return isA(ref, cached);
    }
    return isA(ref, keyOrRef);
}

/**
 * Categorized entity properties. Each entry is [AttributeRef, value]; use attr.id for logic, attr.displayName for labels.
 */
export interface CategorizedProperties {
    basic: [EntityRef, any][];
    references: [EntityRef, any][];
    arrays: [EntityRef, any][];
    booleans: [EntityRef, any][];
    system: [EntityRef, any][];
    memberships: [EntityRef, any][];
}

/**
 * Metadata properties that should be excluded from summary view
 */
const METADATA_PROPERTIES = ['_skipReferenceExpansion', '_attributesRequested', '_attributesMerged'];

/**
 * Blacklist of properties we deem useless and never show in the detail summary.
 * Combined with METADATA_PROPERTIES for full exclusion.
 */
const PROPERTY_BLACKLIST = [
    ...METADATA_PROPERTIES,
    'schemas',
    'rawData' // Often a duplicate of the entity
];

/**
 * Default value for a schema attribute type when the API omits the property.
 * Ensures falses, empty strings, and empty arrays are still visible.
 */
function defaultForSchemaType(type: string, multiValued?: boolean): any {
    if (multiValued === true) return [];
    const t = (type || 'string').toLowerCase();
    if (t === 'boolean') return false;
    if (t === 'string' || t === 'datetime') return '';
    if (t === 'integer' || t === 'decimal' || t === 'number') return undefined;
    return undefined; // complex, reference, etc.
}

/**
 * Build an entity object for summary display using an additive process:
 * 1. Start from schema attributes – each attribute gets the API value or an appropriate default
 *    (false for boolean, '' for string, [] for multiValued) so omitted fields are still visible.
 * 2. Add any extra properties from the API that are not in the schema (subject to blacklist).
 * 3. When excludeMetaOnSummary is true, do not add properties whose key starts with _ or $.
 *
 * @param entity Raw entity from API (may omit false, '', [])
 * @param entityType Entity type for context
 * @param schema Schema with attributes array (each { name, type, multiValued }); null = no schema, use entity keys only
 * @param options excludeMetaOnSummary: if true, exclude keys starting with _ or $ on the default (Summary) tab
 */
export function buildEntityForSummaryDisplay(
    entity: any,
    _entityType: string,
    schema: { attributes?: Array<{ name: string; type?: string; multiValued?: boolean }> } | null,
    options?: { excludeMetaOnSummary?: boolean }
): any {
    const excludeMeta = options?.excludeMetaOnSummary === true;
    const merged: Record<string, any> = {};

    if (!entity || typeof entity !== 'object') {
        return merged;
    }

    const schemaAttrNames = new Set<string>();
    if (schema?.attributes && Array.isArray(schema.attributes)) {
        for (const attr of schema.attributes) {
            const name = attr?.name;
            if (name == null || name === '') continue;
            schemaAttrNames.add(name);
            const hasValue = Object.prototype.hasOwnProperty.call(entity, name);
            merged[name] = hasValue ? entity[name] : defaultForSchemaType(attr.type || 'string', attr.multiValued);
        }
    }

    for (const key of Object.keys(entity)) {
        if (PROPERTY_BLACKLIST.includes(key)) continue;
        if (excludeMeta && (key.startsWith('_') || key.startsWith('$'))) continue;
        if (schemaAttrNames.has(key)) continue; // already set from schema
        merged[key] = entity[key];
    }

    return merged;
}

/**
 * GUID fields that should go to system information or be rendered with GuidDisplay
 * Note: 'Id' is NOT included here - it goes to basic information, right after DisplayName
 * Fields ending in 'Id' that contain GUID values will also be detected automatically
 */
const GUID_FIELDS = ['EntraId', 'ObjectSid', 'ObjectId', 'OnPremId', 'OperationId', 'MessageId'];

/**
 * SID fields that should be rendered with SidDisplay
 */
const SID_FIELDS = ['ObjectSid', 'Sid'];

/**
 * Date/time field name patterns
 */
const DATE_TIME_FIELDS = ['created', 'modified', 'lastmodified', 'date', 'time', 'timestamp', 'expires', 'birthday', 'birthdate'];

/**
 * Check if a value is a reference object (SCIM ref or entity summary).
 * Requires $ref, $type, or both Id and DisplayName so that plain JSON objects
 * with only an "Id" (or "id") field are not misclassified as references.
 */
export function isReferenceObject(value: any): boolean {
    if (!value || typeof value !== 'object') return false;
    if (value.$ref || value.$type) return true;
    const hasId = value.Id != null || value.id != null;
    const hasDisplayName = value.DisplayName != null || value.displayName != null;
    return !!(hasId && hasDisplayName);
}

/**
 * Check if a property key is a GUID field
 * Also checks if field ends with 'Id' or 'Guid' and contains a GUID value
 */
export function isGuidField(key: string, value?: any): boolean {
    const keyLower = key?.toLowerCase();
    if (GUID_FIELDS.some(field => keyLower === field.toLowerCase())) {
        return true;
    }
    // Also check if field ends with 'Id' or 'Guid' and value is a GUID
    if (value !== undefined && value !== null && (keyLower.endsWith('id') || keyLower.endsWith('guid'))) {
        const valueStr = String(value);
        if (isGuid(valueStr)) {
            return true;
        }
    }
    return false;
}

/**
 * Check if a property key is a SID field
 */
export function isSidField(key: string): boolean {
    const keyLower = key?.toLowerCase();
    return SID_FIELDS.some(field => keyLower === field.toLowerCase());
}

/**
 * Check if a string is an ISO 8601 date/time
 */
export function isIsoDateTime(str: string): boolean {
    if (typeof str !== 'string') return false;
    // Match patterns like "2025-11-18T13:40:58.333" or "1975-05-06T00:00:00"
    const isoDateTimePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})?$/;
    return isoDateTimePattern.test(str);
}

/**
 * Check if a field key suggests it contains date/time data
 */
export function isDateTimeField(key: string): boolean {
    const keyLower = key?.toLowerCase() || '';
    return DATE_TIME_FIELDS.some(field => keyLower.includes(field));
}

/**
 * Check if a value should be rendered as a GUID
 * Combines field name check with value validation
 */
export function shouldRenderAsGuid(key: string, value: any): boolean {
    if (value === null || value === undefined) return false;

    const valueStr = String(value);

    // Don't treat SIDs as GUIDs
    if (isSid(valueStr)) return false;

    // Check if it's a GUID value
    const isGuidValue = isGuid(valueStr) || /^[0-9a-f]{32}$/i.test(valueStr.replace(/[^0-9a-f]/gi, ''));

    if (!isGuidValue) return false;

    // Check if field name suggests it's a GUID field
    return isGuidField(key, value);
}

/**
 * Check if a value should be rendered as a Windows SID
 * Combines field name check with value validation
 */
export function shouldRenderAsSid(key: string, value: any): boolean {
    if (!isSidField(key)) return false;
    if (value === null || value === undefined) return false;

    const valueStr = String(value);
    return isSid(valueStr);
}

/**
 * Check if a value should be rendered as a date/time
 * When called with just a value, checks if it looks like an ISO date
 * When called with key and value, also checks if the field name suggests a date
 */
export function shouldRenderAsDateTime(keyOrValue: string | any, value?: any): boolean {
    // If only one argument, treat it as the value and just check if it's an ISO date
    if (value === undefined) {
        return typeof keyOrValue === 'string' && isIsoDateTime(keyOrValue);
    }

    // If two arguments, check both field name and value
    const key = keyOrValue as string;
    if (!isDateTimeField(key)) return false;
    return typeof value === 'string' && isIsoDateTime(value);
}

/**
 * Categorize entity properties for display
 */
export function categorizeEntityProperties(
    entity: any,
    entityType: string
): CategorizedProperties {
    const categorizedProps: CategorizedProperties = {
        basic: [],
        references: [],
        arrays: [],
        booleans: [],
        system: [],
        memberships: []
    };

    if (!entity) return categorizedProps;

    const properties = Object.entries(entity);
    const isSetTransitionEntity = isSetTransition(entityType);
    const isWorkflowEntity = isWorkflow(entityType);
    const isWorkflowActivityEntity = isWorkflowActivity(entityType);
    const isPhotoEntity = isPhoto(entityType);

    properties.forEach(([key, value]) => {
        const keyLower = key?.toLowerCase?.() || '';

        // Skip metadata properties in summary view
        if (METADATA_PROPERTIES.includes(key)) {
            return;
        }

        // Skip only truly internal properties
        if (isARef(createEntityRef(key), 'schemas')) {
            return;
        }

        // Skip Data property for Photo entity types (already displayed as photo image)
        if (isPhotoEntity && isARef(createEntityRef(key), 'Data')) {
            return;
        }

        if (value === null || value === undefined) {
            return; // Skip null/undefined values
        }

        const attrRef = createEntityRef(key);
        // Id always goes to basic information (right after DisplayName) - handle early
        if (isARef(attrRef, 'Id')) {
            const alreadyHasId = categorizedProps.basic.some(([a]) => isA(a, 'Id'));
            if (!alreadyHasId) {
                categorizedProps.basic.push([attrRef, value]);
            }
            return; // Skip further processing
        }

        // Special handling for membership properties
        if ((isARef(attrRef, 'MemberOf') || isARef(attrRef, 'MemberOfTransitive')) && Array.isArray(value)) {
            categorizedProps.memberships.push([attrRef, value]);
        } else if (isARef(attrRef, 'emails') && Array.isArray(value)) {
            categorizedProps.arrays.push([attrRef, value]);
        } else if (isARef(attrRef, 'phoneNumbers') && Array.isArray(value)) {
            categorizedProps.arrays.push([attrRef, value]);
        } else if (isARef(attrRef, 'name') && typeof value === 'object' && value !== null) {
            categorizedProps.basic.push([attrRef, value]);
        } else if (isARef(attrRef, 'meta') && typeof value === 'object' && value !== null) {
            categorizedProps.system.push([attrRef, value]);
        } else if (typeof value === 'boolean') {
            categorizedProps.booleans.push([attrRef, value]);
        } else if (Array.isArray(value)) {
            if (value.length > 0 && typeof value[0] === 'object' && value[0] !== null) {
                // Check if it's an array of references (same rule as isReferenceObject: $ref/$type or Id+DisplayName)
                const firstItem = value[0] as any;
                if (isReferenceObject(firstItem)) {
                    categorizedProps.references.push([attrRef, value]);
                } else {
                    categorizedProps.arrays.push([attrRef, value]);
                }
            } else {
                categorizedProps.arrays.push([attrRef, value]);
            }
        } else if (typeof value === 'object' && value !== null) {
            // Force Modification to stay in basic section
            if (isARef(attrRef, 'modification')) {
                categorizedProps.basic.push([attrRef, value]);
                return;
            }

            // catalogId: treat as reference to FunctionCatalog (API does not expose it as entity; has Id and DisplayName)
            if (isARef(attrRef, 'catalogId')) {
                const ref = (value as any);
                categorizedProps.references.push([attrRef, {
                    Id: ref.Id ?? ref.id ?? '',
                    DisplayName: ref.DisplayName ?? ref.displayName ?? ref.Id ?? ref.id ?? '',
                    EntityType: 'FunctionCatalog'
                }]);
                return;
            }

            // Check if it's a reference object
            if (isReferenceObject(value)) {
                categorizedProps.references.push([attrRef, value]);
            } else if (isARef(attrRef, 'LegacyFilter')) {
                categorizedProps.system.push([attrRef, value]);
            } else {
                categorizedProps.basic.push([attrRef, value]);
            }
        } else if (typeof value === 'string') {
            // Debug logging for Operations/Operation field categorization
            const isOperationsOrOperationField = isARef(attrRef, 'Operations') || isARef(attrRef, 'Operation');
            if (isOperationsOrOperationField) {
                // Compute categorization logic without type narrowing issues
                const keyStr = key as string;
                const willGoToSystem = (isARef(attrRef, 'Type') && !isWorkflowEntity) ||
                    isARef(attrRef, 'EntityType') || isARef(attrRef, 'Version') ||
                    isARef(attrRef, 'SqlStatement') || isARef(attrRef, 'SqlStatementExplicit') ||
                    isGuidField(key, value);
                const willGoToBasic = !(isARef(attrRef, 'Type') && !isWorkflowEntity) &&
                    !isARef(attrRef, 'EntityType') && !isARef(attrRef, 'Version') &&
                    !isARef(attrRef, 'SqlStatement') && !isARef(attrRef, 'SqlStatementExplicit') &&
                    !isGuidField(key, value) &&
                    !(isSetTransitionEntity && isARef(attrRef, 'ActionWorkflow')) &&
                    !(isSetTransitionEntity && isARef(attrRef, 'TargetSet'));

                debugVerboseLog('[entityPropertyUtils] Categorizing Operations field:', {
                    key,
                    valueType: typeof value,
                    valueLength: value.length,
                    valuePreview: value.substring(0, 100),
                    isSetTransitionEntity,
                    isWorkflowEntity,
                    isGuidFieldCheck: isGuidField(key, value),
                    willGoToSystem,
                    willGoToBasic
                });
            }

            // Handle string values that might be references for SetTransition
            if (isSetTransitionEntity && isARef(attrRef, 'ActionWorkflow')) {
                categorizedProps.references.push([attrRef, {
                    Id: value,
                    DisplayName: value,
                    EntityType: 'Workflow'
                }]);
            } else if (isSetTransitionEntity && isARef(attrRef, 'TargetSet')) {
                categorizedProps.references.push([attrRef, {
                    Id: value,
                    DisplayName: value,
                    EntityType: 'Set'
                }]);
            } else if (isARef(attrRef, 'catalogId')) {
                categorizedProps.references.push([attrRef, {
                    Id: value,
                    DisplayName: value,
                    EntityType: 'FunctionCatalog'
                }]);
            } else if (isARef(attrRef, 'Type') && !isWorkflowEntity) {
                categorizedProps.system.push([attrRef, value]);
            } else if (isARef(attrRef, 'EntityType') || isARef(attrRef, 'Version') ||
                isARef(attrRef, 'SqlStatement') || isARef(attrRef, 'SqlStatementExplicit') ||
                isARef(attrRef, '$debug') || isARef(attrRef, '_query')) {
                categorizedProps.system.push([attrRef, value]);
            } else if (isGuidField(key, value)) {
                categorizedProps.system.push([attrRef, value]);
            } else if (isARef(attrRef, 'LegacyFilter')) {
                categorizedProps.system.push([attrRef, value]);
            } else {
                categorizedProps.basic.push([attrRef, value]);
            }

            // Log where Operations ended up
            if (isOperationsOrOperationField) {
                const endedUpInBasic = categorizedProps.basic.some(([a]) => isA(a, key));
                const endedUpInSystem = categorizedProps.system.some(([a]) => isA(a, key));
                debugVerboseLog('[entityPropertyUtils] Operations field categorization result:', {
                    key,
                    endedUpInBasic,
                    endedUpInSystem,
                    basicCount: categorizedProps.basic.length,
                    systemCount: categorizedProps.system.length
                });
            }
        } else {
            // System properties categorization (for non-string values)
            if (isARef(attrRef, 'Type') && isWorkflowEntity) {
                categorizedProps.basic.push([attrRef, value]);
            } else if (isARef(attrRef, 'EntityType') || isARef(attrRef, 'Type') || isARef(attrRef, 'Version') ||
                isARef(attrRef, 'SqlStatement') || isARef(attrRef, 'SqlStatementExplicit') ||
                isARef(attrRef, '$debug') || isARef(attrRef, '_query')) {
                categorizedProps.system.push([attrRef, value]);
            } else if ((isARef(attrRef, 'Config') || isARef(attrRef, 'Settings')) &&
                (isWorkflowActivityEntity || isWorkflowEntity)) {
                categorizedProps.system.push([attrRef, value]);
            } else if (isARef(attrRef, 'Script')) {
                categorizedProps.system.push([attrRef, value]);
            } else if (isGuidField(key, value)) {
                categorizedProps.system.push([attrRef, value]);
            } else if (isARef(attrRef, 'LegacyFilter')) {
                categorizedProps.system.push([attrRef, value]);
            } else {
                categorizedProps.basic.push([attrRef, value]);
            }
        }
    });

    return categorizedProps;
}

/** Stub value shown for non-indexed attributes on detail view (sensitive, large, or file; not requested by default). Display as "Not loaded*" with tooltip "Sensitive, Large, or File value". */
export const NON_INDEXED_PROPERTY_STUB = 'Not loaded*';

function looksLikeApiEntity(entity: any): boolean {
    return !!(entity && typeof entity === 'object' && !Array.isArray(entity) && ('Id' in entity || 'id' in entity));
}

/** True when entity was loaded with an explicit attributes= list (detail/unified screen path). */
export function entityHasAttributesRequestedStamp(entity: unknown): boolean {
    const requested = entity && typeof entity === 'object' && !Array.isArray(entity)
        ? (entity as { _attributesRequested?: unknown })._attributesRequested
        : undefined;
    return Array.isArray(requested) && requested.length > 0;
}

/** Dedupe attribute names for attributes= query (case-insensitive; strips leading $.). */
export function dedupeAttributeNamesForRequest(attributes: string[]): string[] {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const raw of attributes) {
        const n = (raw ?? '').toString().trim().replace(/^\$\./, '');
        if (!n) continue;
        const key = n.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(n);
    }
    return out;
}

/**
 * Whether the API request that produced this entity asked for the given attribute (from _attributesRequested stamp).
 * Returns false if entity has no _attributesRequested (e.g. list response or legacy path).
 */
export function wasAttributeRequested(entity: any, attributeName: string): boolean {
    const requested = entity && typeof entity === 'object' && entity._attributesRequested;
    if (looksLikeApiEntity(entity) && (entity._attributesRequested === undefined || entity._attributesRequested === null)) {
        if (process.env.STORYBOOK !== 'true') {
            console.warn('[entityPropertyUtils] Entity data missing _attributesRequested stamp (expected on API response).', { entityId: entity?.Id ?? entity?.id });
        }
    }
    if (!Array.isArray(requested) || requested.length === 0) return false;
    const lower = (attributeName ?? '').toLowerCase();
    return requested.some((a: string) => (a ?? '').toLowerCase() === lower);
}

/**
 * Whether the entity is missing any of the given attributes (by _attributesRequested stamp).
 * Returns true if we need to fetch: entity has no _attributesRequested stamp or empty array, or at least one of
 * neededAttributeNames is not present in entity._attributesRequested (case-insensitive).
 * Use when opening edit to decide whether to call the entity detail API; neededAttributeNames should be the
 * tier3-safe list (indexed + eager non-indexed, excluding large arrays).
 */
export function isEntityMissingAnyRequestedAttributes(entity: any, neededAttributeNames: string[]): boolean {
    if (!neededAttributeNames || neededAttributeNames.length === 0) return false;
    const requested = entity && typeof entity === 'object' && entity._attributesRequested;
    if (!Array.isArray(requested) || requested.length === 0) return true;
    const requestedSet = new Set(requested.map((a: string) => (a ?? '').toLowerCase()));
    const normalizedNeeded = neededAttributeNames.map((n) => (n ?? '').toLowerCase()).filter(Boolean);
    return normalizedNeeded.some((n) => !requestedSet.has(n));
}

/**
 * Whether we should fetch to load the given attribute for this entity.
 * Uses _attributesRequested when present: if the attribute was already requested, we don't fetch again.
 * When _attributesRequested is absent, we need to fetch when the attribute is missing or is the stub value.
 */
export function needsAttributeFetch(entity: any, attributeName: string, stubValue: string = NON_INDEXED_PROPERTY_STUB): boolean {
    if (!entity || typeof entity !== 'object' || Array.isArray(entity)) return true;
    const key = Object.keys(entity).find((k) => (k ?? '').toLowerCase() === (attributeName ?? '').toLowerCase());
    const value = key !== undefined ? entity[key] : undefined;
    const hasRealValue = value !== undefined && value !== null && value !== stubValue;
    if (hasRealValue) return false;
    // If we know this attribute was already requested, don't fetch again (we have the response)
    if (wasAttributeRequested(entity, attributeName)) return false;
    // Missing or stub, and either no stamp or wasn't requested → need fetch
    return true;
}

/**
 * Whether this entity was fetched with skipReferenceExpansion=true (e.g. list/table response).
 * When true, references may be IDs only and we may be missing properties; a fetch with
 * skipReferenceExpansion=false is a good reason to get expanded refs and fuller data for edit.
 */
export function wasFetchedWithSkipReferenceExpansion(entity: any): boolean {
    if (looksLikeApiEntity(entity) && !Object.prototype.hasOwnProperty.call(entity, '_skipReferenceExpansion')) {
        if (process.env.STORYBOOK !== 'true') {
            console.warn('[entityPropertyUtils] Entity data missing _skipReferenceExpansion stamp (expected on API response).', { entityId: entity?.Id ?? entity?.id });
        }
    }
    return !!(entity && typeof entity === 'object' && !Array.isArray(entity) && entity._skipReferenceExpansion === true);
}

/**
 * Build categorized properties from tier3 readable attributes and entity.
 * Uses AttributeRef (id for lookup, displayName for labels). Values are read via getEntityValueByAttributeRef.
 */
export function buildCategorizedPropsFromTier3ReadableAttrs(
    attrsWithTypes: { id: string; displayName: string; name?: string; type: string; indexed?: boolean; array?: boolean; isReference?: boolean }[],
    entity: any,
    _entityType: string
): CategorizedProperties {
    const categorizedProps: CategorizedProperties = {
        basic: [],
        references: [],
        arrays: [],
        booleans: [],
        system: [],
        memberships: []
    };
    if (!attrsWithTypes?.length) return categorizedProps;

    const seen = new Set<string>();
    for (const attr of attrsWithTypes) {
        const id = (attr.id ?? '').trim();
        if (!id || seen.has(id.toLowerCase())) continue;
        seen.add(id.toLowerCase());
        const attrRef: EntityRef = { id, displayName: (attr.displayName ?? id).trim() || id };

        const isNonIndexed = attr.indexed === false;
        let value: unknown;
        if (isNonIndexed) {
            value = entityHasEntityRef(entity, attrRef) ? getEntityValueByEntityRef(entity, attrRef) : NON_INDEXED_PROPERTY_STUB;
            if (value === undefined || value === null) value = NON_INDEXED_PROPERTY_STUB;
        } else {
            value = getEntityValueByEntityRef(entity, attrRef);
            if (value === undefined || value === null) {
                // Tier3 can describe array attributes even when Type is "string" (e.g. MemberTransitive).
                // Default to [] so view can render "Not loaded" / viewer buttons without requiring values.
                if (attr.array === true) {
                    value = [];
                } else {
                    value = defaultForSchemaType(attr.type ?? 'string');
                }
            }
        }

        const keyLower = id.toLowerCase();

        if (isARef(attrRef, 'Id')) {
            categorizedProps.basic.push([attrRef, value ?? '']);
            continue;
        }
        if (isARef(attrRef, 'ObjectId')) {
            categorizedProps.system.push([attrRef, value ?? '']);
            continue;
        }
        if (isARef(attrRef, 'DisplayName')) {
            categorizedProps.basic.push([attrRef, value ?? '']);
            continue;
        }

        if (value === NON_INDEXED_PROPERTY_STUB) {
            categorizedProps.basic.push([attrRef, value]);
            continue;
        }

        // Keep loaded non-indexed attributes in basic so they keep the same section and render with the JSON viewer (e.g. Operations on RequestApproval).
        if (isNonIndexed && (Array.isArray(value) || (typeof value === 'object' && value !== null))) {
            categorizedProps.basic.push([attrRef, value]);
            continue;
        }

        if (typeof value === 'boolean') {
            categorizedProps.booleans.push([attrRef, value]);
        } else if (Array.isArray(value)) {
            // If tier3 says it's a reference array, keep it in references even when empty (value excluded from fetch).
            if (attr.isReference === true || (value.length > 0 && typeof value[0] === 'object' && value[0] !== null && isReferenceObject(value[0]))) {
                categorizedProps.references.push([attrRef, value]);
            } else {
                categorizedProps.arrays.push([attrRef, value]);
            }
        } else if (typeof value === 'object' && value !== null) {
            if (isReferenceObject(value)) {
                categorizedProps.references.push([attrRef, value]);
            } else if (isARef(attrRef, 'LegacyFilter')) {
                categorizedProps.system.push([attrRef, value]);
            } else {
                categorizedProps.basic.push([attrRef, value]);
            }
        } else {
            if (isARef(attrRef, 'EntityType') || isARef(attrRef, 'Type') || isARef(attrRef, 'Version') || isARef(attrRef, 'LegacyFilter')) {
                categorizedProps.system.push([attrRef, value]);
            } else {
                categorizedProps.basic.push([attrRef, value]);
            }
        }
    }

    const basic = categorizedProps.basic;
    const idIdx = basic.findIndex(([a]) => isA(a, 'Id'));
    const dnIdx = basic.findIndex(([a]) => isA(a, 'DisplayName'));
    if (idIdx > 0 || dnIdx > 0) {
        const idEntry = idIdx >= 0 ? basic[idIdx] : null;
        const dnEntry = dnIdx >= 0 ? basic[dnIdx] : null;
        const rest = basic.filter((_, i) => i !== idIdx && i !== dnIdx);
        categorizedProps.basic = [...(idEntry ? [idEntry] : []), ...(dnEntry ? [dnEntry] : []), ...rest];
    }

    return categorizedProps;
}

/**
 * Check if entity has location data that can be mapped
 */
export function hasLocationData(entity: any): boolean {
    return entity && (
        entity.Address ||
        entity.City ||
        entity.State ||
        entity.Zip ||
        (entity.Latitude && entity.Longitude)
    );
}

/**
 * Get display value for an entity (DisplayName or Id)
 */
export function getEntityDisplayValue(entity: any): string {
    if (!entity) return '';
    return entity.DisplayName || entity.displayName || entity.Name || entity.name ||
        entity.Id || entity.id || '';
}

/**
 * Get ID from an entity or reference
 */
export function getEntityId(entity: any): string {
    if (!entity) return '';
    if (typeof entity === 'string') return entity;
    return entity.Id || entity.id || entity.$ref || '';
}

/**
 * Get basic property count for display
 */
export function getPropertyCounts(categorized: CategorizedProperties): {
    total: number;
    basic: number;
    references: number;
    arrays: number;
    booleans: number;
    system: number;
    memberships: number;
} {
    return {
        total: categorized.basic.length + categorized.references.length +
            categorized.arrays.length + categorized.booleans.length +
            categorized.system.length + categorized.memberships.length,
        basic: categorized.basic.length,
        references: categorized.references.length,
        arrays: categorized.arrays.length,
        booleans: categorized.booleans.length,
        system: categorized.system.length,
        memberships: categorized.memberships.length
    };
}

